import { z } from "zod"

import { fieldBeforeAfterSetV2Schema } from "./field-before-after-v2.js"
import {
  fieldProjectionV5Schema,
  fieldVisitProjectionV5Schema,
} from "./field-projection-v5.js"

export const fieldVisitProjectionV6Schema = z
  .object({
    ...fieldVisitProjectionV5Schema.shape,
    beforeAfterSets: z.array(fieldBeforeAfterSetV2Schema).max(25),
  })
  .strict()

export const fieldProjectionV6Schema = z
  .object({
    ...fieldProjectionV5Schema.shape,
    responseVersion: z.literal(6),
    visits: z.array(fieldVisitProjectionV6Schema).max(100),
  })
  .strict()
  .superRefine((projection, context) => {
    const generated = Date.parse(projection.generatedAt)
    const expires = Date.parse(projection.expiresAt)
    if (expires <= generated || expires - generated > 15 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        message: "Field projection expiry must be within 15 minutes.",
        path: ["expiresAt"],
      })
    }
    const start = Date.parse(`${projection.horizonStartDate}T00:00:00Z`)
    const end = Date.parse(`${projection.horizonEndDate}T00:00:00Z`)
    if (end - start !== 2 * 24 * 60 * 60 * 1_000) {
      context.addIssue({
        code: "custom",
        message: "Field projection horizon must be today and the next day.",
        path: ["horizonEndDate"],
      })
    }
    if (
      new Set(projection.visits.map((visit) => visit.visitId)).size !==
      projection.visits.length
    ) {
      context.addIssue({
        code: "custom",
        message: "Field projection Visits must be unique.",
        path: ["visits"],
      })
    }
    if (
      projection.workShift &&
      !projection.relationships.some(
        (relationship) =>
          relationship.relationshipId ===
            projection.workShift?.relationshipId &&
          relationship.legalEntityId === projection.workShift.legalEntityId,
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Active Work Shift must use a current relationship.",
        path: ["workShift"],
      })
    }
    if (
      projection.visitTime &&
      (!projection.workShift ||
        projection.visitTime.workShiftId !== projection.workShift.workShiftId)
    ) {
      context.addIssue({
        code: "custom",
        message: "Visit Time must be nested in the active Work Shift.",
        path: ["visitTime"],
      })
    }
    if (
      projection.visitTime &&
      !projection.relationships.some(
        (relationship) =>
          relationship.legalEntityId === projection.visitTime?.legalEntityId &&
          relationship.branchIds.includes(projection.visitTime.branchId),
      )
    ) {
      context.addIssue({
        code: "custom",
        message: "Visit Time must remain inside the current worker scope.",
        path: ["visitTime"],
      })
    }
    for (const [visitIndex, visit] of projection.visits.entries()) {
      const setIds = visit.beforeAfterSets.map((set) => set.setId)
      if (new Set(setIds).size !== setIds.length) {
        context.addIssue({
          code: "custom",
          message: "Before/After sets must be unique within a Visit.",
          path: ["visits", visitIndex, "beforeAfterSets"],
        })
      }
    }
  })

export type FieldProjectionV6 = z.infer<typeof fieldProjectionV6Schema>
export type FieldVisitProjectionV6 = z.infer<
  typeof fieldVisitProjectionV6Schema
>
